import React, { useState, useEffect } from 'react';
import { Formik, Form, useFormikContext } from 'formik';
import * as Yup from 'yup';
import { Flex, Button, useToast, FormControl } from '@chakra-ui/react';
import { EditIcon, CloseIcon } from '@chakra-ui/icons';
import Card from 'components/card/Card';
import { useLocation } from 'react-router-dom';
import SelectionTables from './components/selectionTables';
import { useSelectedPermissions } from 'contexts/PermissionGroupArrayContext';
import PermissionGroupApi from 'api/permissiongroup';
import InputField from 'components/formComponents/inputField';
import SelectField from 'components/formComponents/selectField';
import { PermissionGroupValidationSchema } from 'formValidations/permissionGroupFormValidations';
import { usePermissions } from 'contexts/PermissionContext';
import { Modules } from 'config/constant';
import NoPermissionsMessage from 'components/nopermission/NoPermissions';
import { isValidUUID } from 'utils/fetchUtils';
import PageError from 'components/nopermission/PageError';

const NameField = ({ disabled, stateDisabled }) => {
  const { values, setFieldValue } = useFormikContext();
  const [name, setName] = useState(values.name);

  useEffect(() => {
    setFieldValue('name', name);
  }, [name, setFieldValue]);

  useEffect(() => {
    setName(values.name);
  }, [values.name]);

  return (
    <InputField
      label='Name of the Permission Group'
      required={!disabled && true}
      name='name'
      type='text'
      value={name}
      disabled={disabled || stateDisabled}
      onChange={(e) => setName(e.target.value)}
    />
  );
};

const PermissionGroup = () => {
  const groupId = IdFromUrl(window.location.href);
  function IdFromUrl(url) {
    // Split the URL by slashes
    const parts = url.split('/');

    // The last part of the URL is the ID
    const id = parts[parts.length - 1];

    return id;
  }
  console.log('group id from props', groupId);
  const toast = useToast();

  const [loading, setLoading] = useState(false);
  const [permissionGroupData, setPermissionGroupData] = useState(null);
  const [disabled, setDisabled] = useState(true);
  const { getPermissionsForModule } = usePermissions();
  const [permissions, setPermissions] = useState(null);
  const [fetchPermissionGroupDataError, setFetchPermissionGroupDataError] =
    useState(false);

  useEffect(() => {
    const permissions = getPermissionsForModule([Modules.PermissionGroup]);
    setPermissions(permissions);
  }, []);

  const enable = () => setDisabled(!disabled);
  const disable = () => {
    setDisabled(true);
    FetchPermissionGroupdata();
  };

  const { selectedPermissions, setSelectedPermissions } =
    useSelectedPermissions();

  const getIdArray = (permissions) =>
    permissions.map((permission) => permission.id);

  const [key, setKey] = useState(0);
  const reloadChild = () => setKey((prevKey) => prevKey + 1);

  const FetchPermissionGroupdata = async () => {
    if (!isValidUUID(groupId)) {
      setFetchPermissionGroupDataError(true);
      return;
    }
    try {
      setLoading(true);
      let response = await PermissionGroupApi.ListById({ groupId });
      response = response.data;
      console.log('response from permission Group data', response);
      setLoading(false);
      if (response.status_code === 200) {
        setFetchPermissionGroupDataError(false);
        setPermissionGroupData(response.data);
        setSelectedPermissions(response.data.list);
      } else {
        setFetchPermissionGroupDataError(true);
        setPermissionGroupData({});
      }
    } catch (err) {
      setFetchPermissionGroupDataError(true);
      console.log(err);
    }
  };

  useEffect(() => {
    FetchPermissionGroupdata();
  }, []);

  const initialValues = {
    name: permissionGroupData?.basicdetails?.name,
    status: permissionGroupData?.basicdetails?.is_active,
  };

  const onSubmit = async (values, { setSubmitting }) => {
    console.log(
      'values being submitted-',
      values,
      getIdArray(selectedPermissions)
    );
    try {
      let response = await PermissionGroupApi.Update({
        groupId,
        ...values,
        status: values.status === 'true',
        idArray: getIdArray(selectedPermissions),
      });
      response = response.data;
      if (response.status_code === 200) {
        toast({
          title: response.message || 'Successfully Updated',
          status: 'success',
          duration: 5000,
          isClosable: true,
          position: 'top',
        });
        setDisabled(true);
      } else {
        toast({
          title: response.message || 'Error Updating',
          status: 'error',
          duration: 5000,
          isClosable: true,
          position: 'top',
        });
      }
    } catch (err) {
      toast({
        title: 'Unable to Update',
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'top',
      });
      console.log(err);
    } finally {
      setSubmitting(false);
    }
  };

  console.log('initialValues', initialValues);

  // Custom function to synchronize selectedPermissions with Formik's idArray
  const SyncPermissions = (setFieldValue) => {
    useEffect(() => {
      setFieldValue('idArray', getIdArray(selectedPermissions));
    }, [selectedPermissions, setFieldValue]);
  };
  if (!permissions?.read) {
    return <NoPermissionsMessage module={Modules.PermissionGroup} />;
  }
  return (
    <Flex
      height='100%'
      mt='7rem'
      direction='column'>
      {fetchPermissionGroupDataError ? (
        <PageError />
      ) : (
        permissionGroupData && (
          <Formik
            initialValues={initialValues}
            validationSchema={PermissionGroupValidationSchema}
            onSubmit={onSubmit}>
            {({ isSubmitting, setFieldValue }) => {
              SyncPermissions(setFieldValue);

              return (
                <Form noValidate>
                  <Flex
                    direction='row'
                    width='100%'
                    justify='space-between'>
                    <Card
                      bgColor='gray.200'
                      maxWidth='5xl'>
                      <Flex
                        width='100%'
                        direction={{ base: 'column', md: 'row' }}
                        justify='space-between'>
                        <Flex width='md'>
                          <NameField
                            disabled={disabled}
                            stateDisabled={isSubmitting}
                          />
                        </Flex>
                        <Flex width='md'>
                          <SelectField
                            label='Status'
                            name='status'
                            disabled={disabled}
                            options={[
                              { value: 'true', label: 'Active' },
                              { value: 'false', label: 'Inactive' },
                            ]}
                          />
                        </Flex>
                      </Flex>
                    </Card>
                    {disabled ? (
                      <EditIcon
                        ml='5'
                        fontSize='large'
                        onClick={enable}
                        cursor='pointer'
                      />
                    ) : (
                      <CloseIcon
                        ml='5'
                        fontSize='large'
                        onClick={disable}
                        cursor='pointer'
                      />
                    )}
                  </Flex>

                  <SelectionTables
                    key={key}
                    disabled={disabled}
                  />

                  {!disabled &&
                    (selectedPermissions.length < 1 ? (
                      <Button
                        mt={20}
                        colorScheme='blue'
                        disabled
                        type='submit'>
                        Please Select atleast 1 Permission
                      </Button>
                    ) : (
                      <Button
                        mt={20}
                        colorScheme='blue'
                        type='submit'
                        disabled={!permissions?.update}
                        isLoading={isSubmitting}>
                        Update Permission Group
                      </Button>
                    ))}
                </Form>
              );
            }}
          </Formik>
        )
      )}
    </Flex>
  );
};

export default PermissionGroup;
